// Copyright (c) Bentley Systems
import { useCallback, useEffect, useRef, useState } from "react";
import { AppDispatch } from "../app/store";
import { ConfigManager } from "../Helpers/config-manager";
import { setErrorState } from "../Helpers/helper-functions";
import {
  indexActiveDebugLogging,
  ProjectDebugLogging,
} from "./Models/project-debug-logging";

/**
 * Outcome of a Debug logging request. `message` is set when ProjectProvider explained the failure
 * and the caller should show it; otherwise the failure has already been reported through the
 * global error state, or the request was cancelled.
 */
export type DebugLoggingResult =
  | { ok: true; state: ProjectDebugLogging }
  | { ok: false; message?: string };

const iTwinDebugLoggingUrl = (iTwinId: string) =>
  `${ConfigManager.projectProviderUrl}/api/itwins/${encodeURIComponent(iTwinId)}/debug-logging`;

const projectDebugLoggingUrl = (uniqueName: string, iTwinId: string) =>
  `${ConfigManager.projectProviderUrl}/api/itwins/${encodeURIComponent(iTwinId)}/projects/${encodeURIComponent(uniqueName)}/debug-logging`;

const isAbortError = (error: unknown) =>
  (error as { name?: string })?.name === "AbortError";

/**
 * Reads the message of ProjectProvider's `{ error: { code, message, details? } }` failure body.
 * A field-level detail is more specific than the summary, so the first one wins when present.
 */
const readErrorMessage = async (response: Response): Promise<string | undefined> => {
  try {
    const data = await response.json();
    const detailMessage = data?.error?.details?.[0]?.message;
    if (typeof detailMessage === "string" && detailMessage) {
      return detailMessage;
    }
    const message = data?.error?.message;
    return typeof message === "string" && message ? message : undefined;
  } catch {
    return undefined;
  }
};

type UseITwinDebugLoggingProps = {
  iTwinId: string | undefined;
  accessToken: string;
  dispatch: AppDispatch;
  isEnabled: boolean;
  /** Change to read the grants again, e.g. after one was switched on or off. */
  refreshKey?: number;
};

/**
 * Reads which integration projects have Debug logging on for one iTwin.
 *
 * Only architects who may develop projects can see or change the switch. For anyone else
 * ProjectProvider answers 403 and `isDebugLoggingAvailable` stays false, so the page hides the
 * feature instead of reporting an error. A 404 is treated the same way, so DAW can be deployed
 * before a ProjectProvider that has the endpoint.
 */
export const useITwinDebugLogging = ({
  iTwinId,
  accessToken,
  dispatch,
  isEnabled,
  refreshKey,
}: UseITwinDebugLoggingProps) => {
  const [debugLoggingByProject, setDebugLoggingByProject] = useState<
    Map<string, ProjectDebugLogging>
  >(() => new Map());
  const [isDebugLoggingAvailable, setIsDebugLoggingAvailable] = useState(false);

  useEffect(() => {
    setDebugLoggingByProject(new Map());
    if (!isEnabled || !iTwinId) {
      return;
    }

    const abortController = new AbortController();
    const runId = crypto.randomUUID();

    const load = async () => {
      try {
        const response = await fetch(iTwinDebugLoggingUrl(iTwinId), {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: accessToken,
            RunId: runId,
          },
          signal: abortController.signal,
        });

        if (response.status === 403 || response.status === 404) {
          setIsDebugLoggingAvailable(false);
          return;
        }
        if (!response.ok) {
          setIsDebugLoggingAvailable(false);
          setErrorState(response.status, dispatch, runId, true);
          return;
        }

        const data = await response.json();
        const states: ProjectDebugLogging[] = Array.isArray(data) ? data : [];
        setDebugLoggingByProject(indexActiveDebugLogging(states));
        setIsDebugLoggingAvailable(true);
      } catch (error: unknown) {
        if (!isAbortError(error)) {
          setIsDebugLoggingAvailable(false);
          setErrorState(500, dispatch, runId, true);
        }
      }
    };

    void load();
    return () => {
      abortController.abort();
    };
  }, [iTwinId, accessToken, dispatch, isEnabled, refreshKey]);

  return { debugLoggingByProject, isDebugLoggingAvailable };
};

type UseProjectDebugLoggingApiProps = {
  accessToken: string;
  dispatch: AppDispatch;
};

/**
 * Reads and changes the Debug logging state of one integration project on one iTwin.
 * A change in flight is cancelled when another starts or the caller unmounts.
 */
export const useProjectDebugLoggingApi = ({
  accessToken,
  dispatch,
}: UseProjectDebugLoggingApiProps) => {
  const mutationAbortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      mutationAbortRef.current?.abort();
    };
  }, []);

  const send = useCallback(
    async (
      method: "GET" | "PUT" | "DELETE",
      url: string,
      signal: AbortSignal,
      body?: unknown,
      noContentState?: ProjectDebugLogging,
    ): Promise<DebugLoggingResult> => {
      const runId = crypto.randomUUID();
      try {
        const response = await fetch(url, {
          method,
          headers: {
            "Content-Type": "application/json",
            Authorization: accessToken,
            RunId: runId,
          },
          body: body === undefined ? undefined : JSON.stringify(body),
          signal,
        });

        if (!response.ok) {
          const errorMessage = await readErrorMessage(response);
          if (errorMessage) {
            return { ok: false, message: `${errorMessage} ActivityId="${runId}"` };
          }
          setErrorState(response.status, dispatch, runId, true);
          return { ok: false };
        }

        if (response.status === 204 && noContentState) {
          return { ok: true, state: noContentState };
        }

        const data = await response.json();
        if (!data || typeof data !== "object") {
          setErrorState(500, dispatch, runId, true);
          return { ok: false };
        }
        return { ok: true, state: data as ProjectDebugLogging };
      } catch (error: unknown) {
        if (!isAbortError(error)) {
          setErrorState(500, dispatch, runId, true);
        }
        return { ok: false };
      }
    },
    [accessToken, dispatch],
  );

  const startMutation = useCallback(() => {
    mutationAbortRef.current?.abort();
    const abortController = new AbortController();
    mutationAbortRef.current = abortController;
    return abortController.signal;
  }, []);

  const getDebugLogging = useCallback(
    async (uniqueName: string, iTwinId: string, signal: AbortSignal) =>
      send("GET", projectDebugLoggingUrl(uniqueName, iTwinId), signal),
    [send],
  );

  /** Turns Debug logging on for `durationDays` from now. Calling it again restarts the period. */
  const enableDebugLogging = useCallback(
    async (uniqueName: string, iTwinId: string, durationDays: number) =>
      send("PUT", projectDebugLoggingUrl(uniqueName, iTwinId), startMutation(), {
        durationDays,
      }),
    [send, startMutation],
  );

  /** Turns Debug logging off. ProjectProvider answers 204, so the resulting state is "off". */
  const disableDebugLogging = useCallback(
    async (uniqueName: string, iTwinId: string) =>
      send("DELETE", projectDebugLoggingUrl(uniqueName, iTwinId), startMutation(), undefined, {
        uniqueName,
        iTwinId,
        debugEnabled: false,
      }),
    [send, startMutation],
  );

  return { getDebugLogging, enableDebugLogging, disableDebugLogging };
};
